import "./home.css";

import { TOUR_IDS } from "../config.js";
import { loadCalendarSnapshot, type CalendarEntry } from "../catalog.js";
import { SOON_WINDOW_MS, START_AFTER_PREVIOUS, countdownText } from "../live/round-state.js";
import type { View } from "../router.js";
import { foldSections, roundHref, tourBoard, type TourBand, type TourBoard, type TourCard, type TourShelves } from "../tours.js";
import { EMPTY_CALENDAR, banner, emptyState, masthead, sectionHead, siteFooter } from "../ui/chrome.js";
import { afterNextPaint, h } from "../ui/dom.js";
import { createHomeHotBand } from "../ui/hot-band.js";
import { pillarsSection, supportSection } from "../ui/pillars.js";
import type { BroadcastEntry } from "../gen/catalog_pb.js";
import { localWhen } from "./fixtures-heading.js";
import { waitlist } from "./waitlist.js";
import { prefetchWatchPage } from "./round-route.js";

/**
 * The apex page: every tournament whose winner is not yet decided.
 *
 * ONE read answers it — `Catalog.ListBroadcasts` on the content-service, through
 * the same 60 s memo the round page shares — and everything the grid says about
 * a tournament is derived from that answer in `src/tours.ts`, which is pure and
 * tested. Nothing here opens a live stream: the live plane starts on
 * `/round/:id`, and a page that subscribed per round would open one stream for
 * every round on the wire while a visitor waits for a grid.
 * `src/test/home.imports.test.ts` is the guard that keeps it that way.
 *
 * The GRID renders in ONE pass from ONE clock, and once it is up nothing moves
 * it: there is no ticker. A countdown that moves is worth a timer on a page
 * about a single round, but this page's cards would each have to be
 * re-derived, and a card that changed shelf under the reader's cursor is worse
 * than a minute-stale "in 2h 5m". The one timer in this module belongs to
 * `watchCalendar`, and it runs only BEFORE the grid exists: while the calendar
 * cannot be read, the slot holds the unavailable banner and is re-rendered on
 * every attempt (its countdown and attempt number change). The first resolved
 * answer replaces the slot with the grid, ends the watch and stops the timer;
 * teardown aborts both it and the read in flight.
 */
/** What this page is, in one line: the lede under "Live tournaments". */
export const HOME_LEDE = "Every board, live — from the Olympiad to your local club.";

export const homePage: View = (_route, mount) => {
  const controller = new AbortController();

  // The grid's slot. It starts as the wait — a status line over a skeleton of
  // the first row, so the sections below never occupy card space — and holds
  // whichever of the four answers below has arrived: the banner may replace it
  // once per failed attempt while the calendar is unreadable, and the grid (or
  // the empty state) replaces it once, for good.
  //
  // The skeleton is only ever the WAIT. An answer — the banner included —
  // replaces it and clears `aria-busy`, so no state but the first shows
  // placeholder cards. While `watchCalendar` is still re-asking by itself the
  // slot keeps the skeleton's reserved height (`tours-board-held`), so a
  // retry that lands does not move the page; once it falls back to the button
  // the reservation is dropped (a click is input, and its shifts are exempt).
  const board = h("div", { class: "tours-board tours-board-loading", "aria-busy": "true" },
    h("p", { class: "note", role: "status", text: "Loading tournaments…" }),
    h("div", { class: "tour-grid", "aria-hidden": "true" },
      ...Array.from({ length: EAGER_CARDS }, () => h("div", { class: "tour-placeholder" },
        h("div", { class: "tour-art" }),
        h("div", { class: "tour-placeholder-body" },
          h("span", {}), h("span", {}), h("span", {})),
      )),
    ),
  );
  const showBoard = (held: boolean, ...content: HTMLElement[]): void => {
    board.replaceChildren(...content);
    board.className = held ? "tours-board tours-board-held" : "tours-board";
    board.setAttribute("aria-busy", "false");
  };
  // "Heating up" (hot-games O3, owner 2026-09-23): below the grid, hidden until the SAME
  // snapshot the grid is built from says a live game qualifies, and revealed in
  // the grid's own task — it never delays the first cover or moves the page.
  const hotBand = createHomeHotBand();

  mount.append(
    h(
      "div",
      { class: "shell" },
      masthead(),
      h(
        "section",
        { class: "section tours-section", id: "tournaments", tabindex: -1 },
        h("h1", { class: "page-title", text: "Live tournaments" }),
        // The roof line belongs to the app's section below (`pillarsSection`); set here too, the
        // page said it twice. This lede says what THIS page is.
        h("p", { class: "lede tours-lede", text: HOME_LEDE }),
        board,
        hotBand.root,
      ),
      pillarsSection(),
      // The app announcement belongs with the app section above it, not after Support.
      waitlist(),
      supportSection(),
      siteFooter(),
    ),
  );

  // mirror-of: `shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/HomeSchedulePresentation.kt`
  // (`HomeScheduleStrip`) — the app's three answers, in the app's order:
  // UNAVAILABLE is the only one that licenses a placeholder; RESOLVED may be
  // EMPTY, which is the honest statement "nothing is on" and gets the empty
  // PAIR rather than a warning; and a thrown fetch is unavailability with a
  // reason attached. The seeded bit comes from the server (`overlay_seeded`),
  // so a fed-but-quiet calendar can never be mistaken for a broken one.
  // Agreement test: `src/tours.test.ts` (`describe("tourBoard")`).
  //
  // UNAVAILABLE is not final: `watchCalendar` keeps asking, and the first good
  // answer replaces the banner. The grid itself is still rendered once.
  const { retry } = watchCalendar(
    (outcome) => {
      if (outcome.kind === "unavailable") {
        showBoard(outcome.retryInMs !== null, calendarUnavailable(outcome, retry));
        return;
      }
      if (outcome.board.total === 0) {
        showBoard(false, emptyState(EMPTY_CALENDAR.title, EMPTY_CALENDAR.message));
        return;
      }
      showBoard(false, ...shelfSections(outcome.board.shelves, Date.now()));
      warmWatchPageWhenIdle(board, controller.signal);
    },
    {
      load: async (signal) => {
        const snapshot = await loadCalendarSnapshot(TOUR_IDS, { signal });
        hotBand.offer(snapshot);
        return tourBoard(snapshot, Date.now());
      },
    },
    controller.signal,
  );

  // A pointer on (or focus in) a tournament link is the visitor about to open a watch page: fetch
  // its code now, for the visitor who clicks before the idle prefetch below has run.
  const intent = (event: Event): void => {
    if (!(event.target instanceof Element) || event.target.closest('a[href^="/edition/"], a[href^="/round/"]') === null) return;
    prefetchWatchPage();
  };
  board.addEventListener("pointerover", intent, { signal: controller.signal });
  board.addEventListener("focusin", intent, { signal: controller.signal });

  return () => controller.abort();
};

/**
 * Once the grid's covers are in and the browser is idle, fetch the watch page's code, so the most
 * common next step — opening a tournament — starts from a warm cache. After the covers, never
 * with them: started at the grid's paint, the ~150 kB of board code shared the wire with the
 * eager covers (first cover 920 → 1 103 ms, sequential 7-run medians,
 * `scripts/measure-desktop-journeys.mjs`). Its stylesheets change nothing on this page (0 of 683
 * elements' boxes or styles, 2026-09-30). A visitor who asked to save data gets it only on intent.
 */
function warmWatchPageWhenIdle(board: HTMLElement, signal: AbortSignal): void {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData === true) return;
  const covers = [...board.querySelectorAll<HTMLImageElement>('img[loading="eager"]')].filter((img) => !img.complete);
  const settled = covers.map((img) => new Promise<void>((done) => {
    img.addEventListener("load", () => done(), { once: true, signal });
    img.addEventListener("error", () => done(), { once: true, signal });
  }));
  void Promise.all([afterNextPaint(), ...settled]).then(() => {
    if (signal.aborted) return;
    if (typeof requestIdleCallback === "function") requestIdleCallback(() => { if (!signal.aborted) prefetchWatchPage(); }, { timeout: 3000 });
    else setTimeout(() => { if (!signal.aborted) prefetchWatchPage(); }, 1000);
  });
}

function describeError(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * How the page keeps asking for a calendar it could not get.
 *
 * Behind the transport's own ~10 s retry (`net/retry.ts`) this is the page's
 * lane: a content-service restart is ~31 s of 502s, a relay-ingest restart
 * drops and re-seeds, and a Cloud Run instance answers unseeded until it has
 * read its object. Every five seconds for two minutes covers all three with
 * the banner visible the whole time; past that the page stops spending the
 * visitor's radio and offers a button instead.
 */
export const CALENDAR_REFETCH_MS = 5_000;
export const CALENDAR_AUTO_REFETCH_FOR_MS = 120_000;

export type CalendarOutcome =
  | { kind: "resolved"; board: Extract<TourBoard, { state: "resolved" }> }
  | {
      kind: "unavailable";
      /** The thrown error's message, or null when the server answered unseeded. */
      reason: string | null;
      /** 1-based count of asks so far, the failed one included. */
      attempt: number;
      /** When the page asks again by itself, or null once it is waiting for the button. */
      retryInMs: number | null;
    };

export interface CalendarWatchDeps {
  /**
   * One ask: the memoised snapshot, banded. Throws on a transport failure.
   *
   * Given the watch's own signal, which the read must thread all the way to
   * the fetch: teardown has to cancel the request and the transport's backoff
   * chain in flight, not merely stop rendering what they return.
   */
  load: (signal: AbortSignal) => Promise<TourBoard>;
  now?: () => number;
  intervalMs?: number;
  autoRetryForMs?: number;
}

/**
 * Ask until a resolved board arrives, rendering every outcome on the way.
 *
 * One ask in flight at a time; a resolved answer ends the watch; an aborted
 * signal ends it silently (nothing is rendered or asked after it) and, because
 * the ask is HANDED that signal, cancels the read it is waiting on rather than
 * abandoning it. `retry` is
 * the button: it asks at once, and a failed manual ask opens a fresh auto
 * window rather than falling straight back to the button.
 */
export function watchCalendar(
  render: (outcome: CalendarOutcome) => void,
  deps: CalendarWatchDeps,
  signal: AbortSignal,
): { retry: () => void } {
  const now = deps.now ?? Date.now;
  const intervalMs = deps.intervalMs ?? CALENDAR_REFETCH_MS;
  const autoRetryForMs = deps.autoRetryForMs ?? CALENDAR_AUTO_REFETCH_FOR_MS;
  let attempt = 0;
  let windowOpenedAt = now();
  let inFlight = false;
  let done = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const unavailable = (reason: string | null): void => {
    const automatic = now() - windowOpenedAt < autoRetryForMs;
    render({ kind: "unavailable", reason, attempt, retryInMs: automatic ? intervalMs : null });
    if (automatic) timer = setTimeout(ask, intervalMs);
  };

  function ask(): void {
    timer = undefined;
    if (done || inFlight || signal.aborted) return;
    inFlight = true;
    attempt += 1;
    deps.load(signal).then(
      (answer) => {
        inFlight = false;
        if (signal.aborted) return;
        if (answer.state === "unavailable") {
          unavailable(null);
          return;
        }
        done = true;
        render({ kind: "resolved", board: answer });
      },
      (err: unknown) => {
        inFlight = false;
        if (signal.aborted) return;
        unavailable(describeError(err));
      },
    );
  }

  signal.addEventListener(
    "abort",
    () => {
      clearTimeout(timer);
      timer = undefined;
    },
    { once: true },
  );

  ask();
  return {
    retry: () => {
      if (done || inFlight || signal.aborted) return;
      clearTimeout(timer);
      windowOpenedAt = now();
      ask();
    },
  };
}

/**
 * The banner for a calendar the page could not get, with what happens next:
 * the countdown to the automatic re-ask while the window is open, else the
 * button. The escape hatch — a round can be opened by its id — stays in both.
 */
export function calendarUnavailable(outcome: Extract<CalendarOutcome, { kind: "unavailable" }>, retry: () => void): HTMLElement {
  const reason = outcome.reason === null ? "" : ` (${outcome.reason})`;
  const status = banner(`The calendar is unavailable right now${reason}. Live rounds can still be opened directly at /round/<id>.`);
  const next =
    outcome.retryInMs === null
      ? h("button", { type: "button", class: "btn", onclick: retry, text: "Try again" })
      : h("p", { class: "note", text: `Retrying in ${Math.round(outcome.retryInMs / 1000)}s (attempt ${outcome.attempt})` });
  return h("div", { class: "calendar-unavailable" }, status, next);
}

/** The shelves, in the owner's order. A shelf with no cards renders NOTHING —
 *  a heading over an empty box states an absence nobody asked about. */
const SHELVES: ReadonlyArray<{ key: keyof TourShelves; title: string }> = [
  { key: "live", title: "Live now" },
  { key: "inProgress", title: "In progress" },
  { key: "startingSoon", title: "Starting soon" },
  { key: "ended", title: "Just finished" },
];

/**
 * How many covers are fetched eagerly.
 *
 * Three is the first row at the widest breakpoint. Once the calendar arrives,
 * these covers load eagerly; the first cover is the measured mobile LCP and
 * gets high priority. The other covers keep the browser's default priority.
 */
const EAGER_CARDS = 3;

function shelfSections(shelves: TourShelves, nowMs: number): HTMLElement[] {
  const out: HTMLElement[] = [];
  let eagerUntil = EAGER_CARDS;
  for (const { key, title } of SHELVES) {
    const cards = shelves[key];
    if (cards.length === 0) continue;
    const eager = eagerUntil;
    eagerUntil = 0;
    out.push(
      sectionHead(title, `${cards.length} ${cards.length === 1 ? "tournament" : "tournaments"}`),
      h("ul", { class: "tour-grid" }, ...cards.map((card, i) => tourCard(card, nowMs, i < eager, out.length === 0 && i === 0))),
    );
  }
  return out;
}

/**
 * One tournament.
 *
 * The whole card is one link to the round the tour is best represented by, and
 * the parallel-round chips are a SIBLING of that link: nesting an `<a>` inside
 * an `<a>` is not representable, and a round chip is a second destination, not a
 * detail of the first.
 *
 * The cover is DECORATIVE (`alt=""`): the tournament's name is two lines below
 * it as text, and an alt attribute repeating it would make a screen reader say
 * it twice. The URL is used byte-for-byte — it is a signed Lichess thumbnail and
 * editing its size parameters answers 401 (M 2026-09-15).
 */
export function tourCard(card: TourCard, nowMs: number, eager: boolean, priority = false): HTMLElement {
  // The typographic cover is ALWAYS there, under the picture: while a lazy cover
  // is still on its way, when a signed thumbnail answers 401, and for the tours
  // that carry no art at all, the card shows its own name set as a cover rather
  // than an empty beige frame. A picture that fails is removed, not left broken.
  const cover = h("span", { class: "tour-art-cover", "data-tone": String(coverTone(card.name)), "aria-hidden": "true" },
    h("span", { class: "tour-art-cover-name", text: card.name }));
  const picture =
    card.imageUrl === null
      ? null
      : h("img", {
          src: card.imageUrl,
          width: 800,
          height: 400,
          loading: eager ? "eager" : "lazy",
          decoding: "async",
          ...(priority ? { fetchpriority: "high" } : {}),
          alt: "",
          onerror: (event: Event) => (event.currentTarget as HTMLElement | null)?.remove(),
        });

  // No tier chip: "T3" is an internal ranking, not something a visitor reads.
  const status = h(
    "span",
    { class: "tour-status" },
    bandPill(card.band),
    card.timeControl === null ? null : h("span", { class: "tour-tc", text: card.timeControl }),
  );

  const link = h(
    "a",
    { class: "tour-link", href: card.href },
    h("figure", { class: "tour-art" }, cover, picture),
    h(
      "span",
      { class: "tour-body" },
      status,
      h("span", { class: "tour-name", text: card.name }),
      h("span", { class: "tour-round", text: statusLine(card, nowMs) }),
      card.boardCount === null ? null : h("span", { class: "tour-boards", text: `${card.boardCount} boards` }),
      card.players === null ? null : h("span", { class: "tour-players", text: card.players }),
    ),
  );

  // An EVENT's sections are its tournaments, each with its own live round and
  // its own destination; a chip list of the event's rounds would say nothing
  // about which of its tournaments they belong to, so the two rows are
  // alternatives rather than neighbours.
  if (card.sections.length > 1) {
    return h(
      "li",
      { class: `tour-card tour-card-${card.band}` },
      link,
      h(
        "ul",
        { class: "tour-sections" },
        // One outlined pill per SECTION (`foldSections`), never the live red: the card's own
        // status pill is the one LIVE label (design review 2026-09-23).
        ...foldSections(card.sections).map((section) => h("li", {}, h("a", { class: section.band === "ended" ? "pill pill-finished" : "pill pill-section", href: section.href, text: section.name }))),
      ),
    );
  }

  return h(
    "li",
    { class: `tour-card tour-card-${card.band}` },
    link,
    // One live round is the card's own destination and needs no chip. Two or
    // more are the GCL's parallel matches, where the card can only point at one.
    card.live.length > 1
      ? h(
          "ul",
          { class: "tour-live-chips" },
          ...card.live.map((round) =>
            h(
              "li",
              {},
              h("a", { class: "pill pill-live", href: roundHref(round), text: roundLabel(round.entry) }),
            ),
          ),
        )
      : null,
  );
}

/**
 * The capsule a band is drawn as: LIVE is the one filled object on the site
 * (`styles.css` `.pill-live`), a running or coming tour is outlined in the
 * accent, and an ended one in the secondary ink — the same three capsules the
 * round page's fixture list uses, so a card and a fixture agree about a round.
 */
const pillClass = (band: TourBand): string => (band === "live" ? "pill pill-live" : band === "ended" ? "pill pill-finished" : "pill pill-upcoming");

function bandPill(band: TourBand): HTMLElement {
  if (band === "live") return h("span", { class: pillClass(band) }, h("span", { class: "live-dot" }), "Live");
  return h("span", { class: pillClass(band), text: band === "in-progress" ? "In progress" : band === "ended" ? "Ended" : "Starting soon" });
}

/** How many grounds the typographic cover rotates through (`home.css`, `[data-tone]`). */
export const COVER_TONES = 6;

/**
 * Which ground a tour's typographic cover is set on: stable per name, so a
 * tournament keeps its colour across visits and reloads. Pure, and tested.
 */
export function coverTone(name: string): number {
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return hash % COVER_TONES;
}

/**
 * The two letters an artless tour is shown by. Pure, and tested in
 * `src/tours.test.ts`.
 */
export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/u)
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => (word[0] ?? "").toLocaleUpperCase())
    .join("");
}

/**
 * The round the tour is at, in words — the one sentence a card gets to say what
 * is actually happening.
 *
 * Pure: `nowMs` is the caller's, and the viewer's zone and locale are parameters
 * so the rule can be pinned without a browser. The words themselves are NOT
 * invented here — `localWhen` is the site's one date formatter
 * (`pages/fixtures-heading.ts`) and `countdownText` / `START_AFTER_PREVIOUS` are
 * the round page's (`live/round-state.ts`) — because a second spelling of "in 2h
 * 5m" is how two surfaces start disagreeing about the same round.
 */
export function statusLine(card: TourCard, nowMs: number, timeZone?: string, locale?: string): string {
  if (card.band === "live") {
    const name = roundLabel(card.target.entry);
    return card.boardCount === null ? name : `${name} · ${card.boardCount} boards`;
  }
  const next = card.next;
  if (next !== null) {
    if (card.band === "starting-soon") {
      return next.startsAtMs === null ? START_AFTER_PREVIOUS : `Starts ${when(next, nowMs, timeZone, locale)}`;
    }
    return `Next: ${roundLabel(next.entry)} · ${when(next, nowMs, timeZone, locale)}`;
  }
  // Its hour has passed on the organiser's clock and no board has reached us.
  if (card.underway.length > 0) return `${roundLabel(card.target.entry)} underway`;
  if (card.lastFinished !== null) return `Last: ${roundLabel(card.lastFinished.entry)}`;
  return "";
}

/**
 * When a round starts, with the countdown the app adds inside its soon window —
 * the same rule `startLineFor` applies on the round page, from the same two
 * constants.
 */
function when(round: CalendarEntry, nowMs: number, timeZone?: string, locale?: string): string {
  if (round.startsAtMs === null) return START_AFTER_PREVIOUS;
  const words = localWhen(round.startsAtMs, timeZone, locale);
  const remaining = round.startsAtMs - nowMs;
  return remaining <= SOON_WINDOW_MS ? `${words} · ${countdownText(remaining)}` : words;
}

/**
 * What to call a round. The producer's `round_name` ("Round 11"), falling back
 * to the `title` it sends verbatim — the same fallback the fixture list used,
 * and not a parse: the Lichess parse lives in relay-control.
 */
const roundLabel = (entry: BroadcastEntry): string => entry.roundName || entry.title;
